import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../App';
import { IdeaBoxEntry, Rule, Task, TaskType } from 'konect-model';
import "./TaskEditor.css";
import { MDBBtn, MDBIcon, MDBSwitch, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter } from 'mdb-react-ui-kit';
import TaskEditorNode from './TaskEditorNode';
import UserControlService from '../../services/user-control-service';
import LocalStorageService from '../../services/localstorage-service';
import axios from 'axios';
import { nanoid } from 'nanoid';
import { createPortal } from 'react-dom';
import FileSaver from 'file-saver';
import TaskPropertiesDialog from './TaskPropertiesDialog';
import { parseTabIndentedHierarchy } from '../../utils/text-parser';

export enum ModalState {
    HIDDEN = "HIDDEN",
    EDIT = "EDIT",
    ADD = "ADD" 
}

interface TaskEditorProps {
    hideMenu?: boolean;
}

const TaskEditor2 = (props: TaskEditorProps) => {

    const { project, setProject, editingTask, setEditingTask } = useContext(AppContext);
    const [file, setFile] = useState('');
    const fileInputRef = useRef(null);
    const containerRef = useRef(null);
    const [graphPathData, setGraphPathData] = useState('');
    const [constraintPathData, setConstraintPathData] = useState('');
    const [constraintTextData, setConstraintTextData] = useState<{ x: number, y: number, text: string }[]>([]);
    const [nodes, setNodes] = useState<React.ReactNode>(null);
    const [graphDimensions, setGraphDimensions] = useState({ height: 0, width: 0 });
    const [minimizeLeaves, setMinimizeLeaves] = useState<boolean>(LocalStorageService.getMinimizeLeaves());
    const [collapsedTasksList, setCollapsedTasksList] = useState<string[]>(LocalStorageService.getCollapsedTasksList());
    const [showConstraintArrows, setShowConstraintArrows] = useState(LocalStorageService.getShowConstraintArrows());

    const [persistentContextMenu, setPersistentContextMenu] = useState("");

    // TaskPropertiesDialog state
    const [modalState, setModalState] = useState<ModalState>(ModalState.HIDDEN);

    // Text import modal state
    const [showTextImportModal, setShowTextImportModal] = useState(false);
    const [importText, setImportText] = useState("");

    // drag and drop states
    const [isDragging, setIsDragging] = useState(false);
    let [cloneX, setCloneX] = useState("-5000px");
    let [cloneY, setCloneY] = useState("-5000px");
    const [draggedTask, setDraggedTask] = useState<Task | null>(null);
    const taskModelRef = useRef(project.taskModel);
    const [insertDirection, setInsertDirection] = useState("");


    useEffect(() => {
        if (file) processUpload();
        // processUpload is defined later and is stable; intentionally omitted from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file])

    useEffect(() => {
        LocalStorageService.setMinimizeLeaves(minimizeLeaves);
    }, [minimizeLeaves]);

    useEffect(() => {
        LocalStorageService.setCollapsedTasksList(collapsedTasksList);
    }, [collapsedTasksList]);

    useEffect(() => {
        LocalStorageService.setShowConstraintArrows(showConstraintArrows)
        redrawPaths();
        // redrawPaths is defined later and is stable; intentionally omitted from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showConstraintArrows]);

    useEffect(() => {
        setNodes(renderNodes(project.taskModel.rootTask)); // rerenders task-nodes on project update
        // renderNodes is defined later and is stable; intentionally omitted from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, collapsedTasksList, isDragging]);

    useEffect(() => {
        redrawPaths();
        // redrawPaths is defined later and is stable; intentionally omitted from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes]);

    // Redraw paths when zoom changes (detected via ResizeObserver)
    useEffect(() => {
        const container = document.getElementById('graph-container');
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            // Small delay to ensure layout has settled after zoom
            setTimeout(() => redrawPaths(), 50);
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
        // redrawPaths is defined later and is stable; intentionally omitted from dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showConstraintArrows]);


    /* Drag and Drop */

    // recursively checks if task A in a direct or indirect child of task B
    const isAChildOfB = (a: Task, b: Task): boolean => {
        if (b.subtasks.includes(a)) return true;
        return b.subtasks && b.subtasks.some(subtask => isAChildOfB(a, subtask));
    }

    // helper function to determine, if the currently dragged task is position on a cell -> dragged task would be inserted as a child
    const isPositionedOnCell = (task: Task, event: any): Task | undefined => {
        const cell = document.getElementById(task.id);
        if (cell && event.clientX >= cell.getBoundingClientRect().left &&
            event.clientX <= cell.getBoundingClientRect().right &&
            event.clientY >= cell.getBoundingClientRect().top &&
            event.clientY <= cell.getBoundingClientRect().bottom) {
            return task;
        }
        for (const subtask of task.subtasks) {
            const result = isPositionedOnCell(subtask, event);
            if (result) return result;
        }
        return undefined;
    }

    // helper function to determine, if the currently dragged task is position between to siblings or next to a single task -> draggedTask would be inserted as a sibling
    const isPositionedBetweenCells = (draggedTask: Task, searchTask: Task, event: any, offset: number): { parent: Task, index: number } => {
        // check if the cell is positioned between the cells children
        if (searchTask.subtasks && searchTask.subtasks.length > 1) {
            for (let i = 0; i < searchTask.subtasks.length - 1; i++) {
                const leftRect = document.getElementById(searchTask.subtasks[i].id)?.getBoundingClientRect();
                const rightRect = document.getElementById(searchTask.subtasks[i + 1].id)?.getBoundingClientRect();
                if (!leftRect || !rightRect) break;
                if (event.clientY >= leftRect.top && event.clientY <= leftRect.bottom &&
                    event.clientX > leftRect.right && event.clientX < rightRect.left) {
                    if (searchTask.subtasks[i].id === draggedTask.id) return { parent: new Task(), index: -1 }; // return error response, if task is dragged next to itself
                    if (searchTask.subtasks[i + 1].id === draggedTask.id) return { parent: new Task(), index: -1 }; // return error response, if task is dragged next to itself
                    return { parent: searchTask, index: i + 1 };
                }
            }
        }
        // check if the cell is positioned between the cells grandchildren
        if (searchTask.subtasks && searchTask.subtasks.length > 0) {
            for (const subtask of searchTask.subtasks) {
                const result = isPositionedBetweenCells(draggedTask, subtask, event, offset);
                if (result.index !== -1) return result;
            }
        }
        //check if the cell is positioned besides a cell
        const rect = document.getElementById(searchTask.id)?.getBoundingClientRect();
        if (searchTask.parent && rect) {
            // Check if dropped to the left of a cell
            if (event.clientY >= rect.top && event.clientY <= rect.bottom &&
                event.clientX < rect.left && event.clientX > rect.left - offset) {
                const parent = project.taskModel.rootTask.findTask(searchTask.parent);
                if (parent && parent.subtasks[0].id === draggedTask.id) return { parent: new Task(), index: -1 }; // return error response, if task is dragged next to itself
                if (parent) return { parent: parent, index: 0 };
            }
            // Check if is positioned to the right of a cell
            if (event.clientY >= rect.top && event.clientY <= rect.bottom &&
                event.clientX > rect.right && event.clientX < rect.right + offset) {
                const parent = project.taskModel.rootTask.findTask(searchTask.parent)
                if (parent && parent.subtasks[parent.subtasks.length - 1].id === draggedTask.id) return { parent: new Task(), index: -1 }; // return error response, if task is dragged next to itself
                if (parent) return { parent: parent, index: parent.subtasks.length };
            }
        }
        return { parent: new Task(), index: -1 };
    }

    // creates visual clones of the dragged task and all subtask that then are animated towards the dragged task to indicate which tasks are being affected by the drag and drop action
    const renderClones = (task: Task) => {

        const allTasks: Task[] = [];
        const findAllSubtasks = (task: Task) => {
            allTasks.push(task);
            if (task.subtasks.length === 0) return;
            if (task.subtasks && task.subtasks.length > 0) {
                for (const subtask of task.subtasks) findAllSubtasks(subtask)
            }
        }
        findAllSubtasks(task);
        allTasks.shift(); //removes dragged task itself

        let offsetX = 0;
        let offsetY = 0;
        let height = 50;
        let width = 100;
        const cell = document.getElementById(task.id);
        if (cell) {
            offsetX = cell.getBoundingClientRect().left;
            offsetY = cell.getBoundingClientRect().top;
            height = cell.getBoundingClientRect().height;
            width = cell.getBoundingClientRect().width;
        }

        const findPosition = (task: Task, index: number): CSSProperties => {
            const cell = document.getElementById(task.id);
            if (!cell) return {};
            const { left, top } = cell.getBoundingClientRect();
            return {
                position: 'absolute',
                left: `${(index + 1) * 2}px`,
                bottom: `${(index + 1) * 2}px`,
                opacity: `${100 - (index * 20)}%`,
                zIndex: 999 - index,
                height: `${height}px`,
                width: `${width}px`,
                //@ts-ignore
                "--task-slide-x": `${(left - offsetX)}px`,
                //@ts-ignore
                "--task-slide-y": `${(top - offsetY)}px`,
                animation: "task-slide 0.39s ease-in-out forwards",
            };
        }

        return (
            <div style={{ position: 'relative' }}>
                <div id={`${task.id}_clone`} className="graphCell graphCellClone" key={task.id} style={{ zIndex: 1000 }}>
                    <span>{task.name}</span>
                    <div className="graphIconContainer">
                        <div className={`graphIconBorder ${task.type.includes(TaskType.INFORMATION) ? "graphIconInformation" : "graphIconOff"}`}>
                            <MDBIcon fas icon="eye" style={{ color: "#ffffff" }} />
                        </div>
                        <div className={`graphIconBorder ${task.type.includes(TaskType.INTERACTION) ? "graphIconInteraction" : "graphIconOff"}`}>
                            <MDBIcon fas icon="hand-paper" style={{ color: "#ffffff" }} />
                        </div>
                    </div>
                </div>
                {insertDirection && <p className="moveText">{insertDirection === "child" ? "insert as child" : "insert as neighbor"}</p>}
                {(insertDirection === "right" || insertDirection === "left+right") && <div className="moveMenu moveRight">
                    <MDBBtn className={"moveMenuButton justify-content-end align-items-center"}>
                        <i className="fas fa-caret-right"></i>
                    </MDBBtn>
                </div>}
                {insertDirection === "child" && <div className="moveMenu moveDown">
                    <MDBBtn className={"moveMenuButton justify-content-center align-items-end"}>
                        <i className="fas fa-caret-down"></i>
                    </MDBBtn>
                </div>}
                {(insertDirection === "left" || insertDirection === "left+right") && <div className="moveMenu moveLeft">
                    <MDBBtn className={"moveMenuButton justify-content-start align-items-center"}>
                        <i className="fas fa-caret-left"></i>
                    </MDBBtn>
                </div>}
                {!collapsedTasksList.includes(task.id) && allTasks.map((task, index) => {
                    const style = findPosition(task, index);
                    return <div id={`${task.id}_clone`} className="graphCell graphCellClone" key={task.id} style={style}>
                        <span>{task.name}</span>
                        <div className="graphIconContainer">
                        <div className={`graphIconBorder ${task.type.includes(TaskType.INFORMATION) ? "graphIconInformation" : "graphIconOff"}`}>
                                <MDBIcon fas icon="eye" style={{ color: "#ffffff" }} />
                            </div>
                            <div className={`graphIconBorder ${task.type.includes(TaskType.INTERACTION) ? "graphIconInteraction" : "graphIconOff"}`}>
                                <MDBIcon fas icon="hand-paper" style={{ color: "#ffffff" }} />
                            </div>
                        </div>
                    </div>
                })}
            </div>)
    }

    let fadedCells: HTMLElement[] = [];

    // fades all task that are being dragged to indicate which nodes are affected
    const fadeCell = (task: Task) => {
        const cell = document.getElementById(task.id)
        if (!cell) return;
        cell.style.opacity = '50%';
        fadedCells.push(cell);
        if (task.subtasks) task.subtasks.forEach(subtask => fadeCell(subtask));
    }

    const clearFadedCells = () => {
        fadedCells.forEach(cell => cell.style.opacity = '100%');
        fadedCells = [];
    }

    let highlightedCells: HTMLElement[] = [];
    const highlightColor = "#8cdce9"

    // highlights cells that would be adjacent, if the currently dragged task would be dropped at the current position
    const highlightCells = (cells: HTMLElement[]) => {
        for (const cell of cells) {
            cell.style.backgroundColor = highlightColor;
            highlightedCells.push(cell);
        }
    }

    const clearHighlightedCells = () => {
        highlightedCells.forEach(cell => cell.style.backgroundColor = '');
        highlightedCells = [];
    }

    //during drag and drop: scroll graphContainer, when mouse is moved towards the edge
    const scrollOnMovement = (mouseMoveEvent: MouseEvent) => {
        const container = document.getElementById('graph-container');
        const containerRect = container?.getBoundingClientRect();
        if (!container || !containerRect) return;
        const x = mouseMoveEvent.clientX - containerRect.left; // x position within the element
        const y = mouseMoveEvent.clientY - containerRect.top; // y position within the element
        const width = containerRect.width;
        const height = containerRect.height;
        const scrollAmount = 20; // Adjust scroll speed if needed
        const scrollEdge = 0.05;
        // Check if mouse is within the left edge
        if (container.scrollLeft > 0 && x < width * scrollEdge) {
            container.scrollLeft -= scrollAmount;
        }
        // Check if mouse is within the right edge
        if (container.scrollLeft < container.scrollWidth - container.clientWidth && x > width * (1 - scrollEdge)) {
            container.scrollLeft += scrollAmount;
        }
        // Check if mouse is within the top edge
        if (container.scrollTop > 0 && y < height * scrollEdge) {
            container.scrollTop -= scrollAmount;
        }
        // Check if mouse is within the bottom edge
        if (container.scrollTop < container.scrollHeight - container.clientHeight && y > height * (1 - scrollEdge)) {
            container.scrollTop += scrollAmount;
        }
    }

    // main drag and drop event handler functions
    const handleMouseDown = (mouseDownEvent: any) => {
        if (mouseDownEvent.detail !== 1) return;
        if (!mouseDownEvent.target.classList.contains('graphCell')) return;

        setIsDragging(true);
        const draggedTask = project.taskModel.rootTask.findTask(mouseDownEvent.target.id);
        if (!draggedTask) return;
        taskModelRef.current = project.taskModel;
        setDraggedTask(draggedTask);
        fadeCell(draggedTask);
        setCloneX(`${mouseDownEvent.pageX - mouseDownEvent.offsetX}px`);
        setCloneY(`${mouseDownEvent.pageY - mouseDownEvent.offsetY}px`);
        const cloneCell = document.getElementById(`${draggedTask.id}_clone`);
        if (!cloneCell) return;
        
        // mouse move = drag and drop movement (drag)
        const handleMouseMove = (mouseMoveEvent: any) => {
            scrollOnMovement(mouseMoveEvent); // scroll on movement at the edge of the container
            setCloneX(`${mouseMoveEvent.pageX - mouseDownEvent.offsetX}px`);
            setCloneY(`${mouseMoveEvent.pageY - mouseDownEvent.offsetY}px`);
            // Check potential targets during dragging
            const targetTask = isPositionedOnCell(project.taskModel.rootTask, mouseMoveEvent);
            // When hovering on a single cell, highlight it and set insert direction to "child"
            if (targetTask && targetTask.id !== draggedTask.id && !isAChildOfB(targetTask, draggedTask)) {
                clearHighlightedCells();
                const cell = document.getElementById(targetTask.id);
                if (cell) highlightCells([cell, cloneCell]);
                setInsertDirection("child");
            // Check if current position is closely left or right of a cell or between two siblings
            } else if (!targetTask) {
                clearHighlightedCells();
                const { parent, index } = isPositionedBetweenCells(draggedTask, project.taskModel.rootTask, mouseMoveEvent, (mouseDownEvent.target.clientWidth / 2) + 30);
                // cell is positioned to the very left of the siblings
                if (index === 0 && !isAChildOfB(parent.subtasks[0], draggedTask)) {
                    const cell = document.getElementById(parent.subtasks[index].id);
                    if (cell) highlightCells([cell, cloneCell]);
                    setInsertDirection("right");
                // cell is positioned to the very right of the siblings
                } else if (index === parent.subtasks.length && !isAChildOfB(parent.subtasks[0], draggedTask)) {
                    const cell = document.getElementById(parent.subtasks[index - 1].id);
                    if (cell) highlightCells([cell, cloneCell]);
                    setInsertDirection("left");
                // cell is positioned somewhere in between the siblings
                } else if (index >= 0 && !isAChildOfB(parent.subtasks[0], draggedTask)) {
                    const left = document.getElementById(parent.subtasks[index - 1].id);
                    const right = document.getElementById(parent.subtasks[index].id);
                    if (left && right) highlightCells([left, right, cloneCell]);
                    setInsertDirection("left+right");
                } else {
                    clearHighlightedCells();
                    setInsertDirection("");
                }
            }
        }
        document.addEventListener('mousemove', handleMouseMove);

        // mouse up = drag and drop end (drop)
        const handleMouseUp = (mouseUpEvent: any) => {
            setIsDragging(false);
            clearHighlightedCells();
            clearFadedCells();
            UserControlService.update();
            if (taskModelRef.current) project.taskModel = taskModelRef.current;
            setProject(project);
            document.removeEventListener('mousemove', handleMouseMove);
            // 1st check, if the cell was dropped on another cell
            const targetTask = isPositionedOnCell(project.taskModel.rootTask, mouseUpEvent);
            const isTargetNotDragged = (targetTask && targetTask.id !== draggedTask.id)
            if (isTargetNotDragged && !isAChildOfB(targetTask, draggedTask)) {
                UserControlService.update();
                const oldParent = project.taskModel.rootTask.findTask(draggedTask.parent);
                if (oldParent) oldParent.subtasks = oldParent.subtasks.filter(task => task.id !== draggedTask.id);
                draggedTask.parent = targetTask.id;
                targetTask.subtasks.push(draggedTask);
                setProject(project);
            } else if (isTargetNotDragged || !targetTask) { // 2nd check if the cell was dropped between two sibling cells
                let { parent, index } = isPositionedBetweenCells(draggedTask, project.taskModel.rootTask, mouseUpEvent, (mouseDownEvent.target.clientWidth / 2) + 30);
                if (index < 0 || isAChildOfB(parent.subtasks[0], draggedTask)) return;
                UserControlService.update();
                const oldParent = project.taskModel.rootTask.findTask(draggedTask.parent);
                if (oldParent && parent.id === oldParent.id && parent.subtasks.indexOf(draggedTask) < index) index = index - 1; // reduce splice index, if the task is moved within the same parent task
                if (oldParent) oldParent.subtasks = oldParent.subtasks.filter(task => task.id !== draggedTask.id);
                draggedTask.parent = parent.id;
                parent.subtasks.splice(index, 0, draggedTask);
                setProject(project);
            }

        }
        document.addEventListener('mouseup', handleMouseUp, { once: true });
    }

    // adds event listeners for drag and drop; removes them on component unmount
    useEffect(() => {
        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            if (isDragging) {
                document.querySelector('.graphCellClone')?.remove();
                setIsDragging(false);
            }
        }
        // Event listener re-registered when project changes. handleMouseDown and isDragging accessed via closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]);


    /* Rendering */

    // rerenders the path and constraint SVGs. This needs to be done, when the nodes change.
    const redrawPaths = () => {
        const nodes = document.getElementById("node-container");
        if (nodes) {
            const rect = nodes.getBoundingClientRect();
            setGraphDimensions({ height: rect.height, width: rect.width });
        }
        setGraphPathData(calculateGraphPathData()); // recalculates the paths on node update
        setConstraintPathData(showConstraintArrows ? calculateConstraintPathData() : "");
        if (!showConstraintArrows) setConstraintTextData([]);
    }

    // recursively calculates the paths between the parent-nodes and their children
    const calculateGraphPathData = () => {
        let newPathData = '';

        const calculatePosition = (task: Task) => {
            const element = document.querySelector("#graph-container [id='" + task.id + "']");
            if (!element) return;
            const { top, left, width, height } = element.getBoundingClientRect();

            const container = document.getElementById("graph-container");
            if (!container) return;
            const containerRect = container.getBoundingClientRect();

            // Including horizontal scroll offset
            const scrollLeft = container.scrollLeft;
            const scrollTop = container.scrollTop;

            // Adjusting for scroll position
            const taskBottom = top - containerRect.top + height + scrollTop;
            const taskCenterX = left - containerRect.left + width / 2 + scrollLeft;
            const taskLeftX = left - containerRect.left + 10 + scrollLeft;

            task.subtasks.forEach(subtask => {
                const subElement = document.querySelector("#graph-container [id='" + subtask.id + "']");
                if (!subElement) return;

                const subRect = subElement.getBoundingClientRect();
                //if all subtasks are leaves, change display mode
                if (minimizeLeaves && task.subtasks.every((subtask: Task) => subtask.subtasks.length === 0)) {
                    const subCenterY = subRect.top - containerRect.top + subRect.height / 2 + scrollTop;
                    const subLeftX = subRect.left - containerRect.left + scrollLeft;

                    newPathData += `M ${taskLeftX},${taskBottom} V ${subCenterY} H ${subLeftX}`;
                } else {
                    const subTop = subRect.top - containerRect.top + scrollTop;
                    const subCenterX = subRect.left - containerRect.left + subRect.width / 2 + scrollLeft;

                    newPathData += `M ${taskCenterX},${taskBottom} V ${subTop - 10} H ${subCenterX} V ${subTop} `;
                }
            });
            task.subtasks.forEach(subtask => calculatePosition(subtask));
        }
        calculatePosition(project.taskModel.rootTask);
        return newPathData;
    }

    // recursively calculates the constraint paths (e.g. "enabling") between nodes
    const calculateConstraintPathData = () => {
        let newPathData = '';

        let depth = 0;
        const allTextPositions: { x: number, y: number, text: string }[] = []

        const calculatePosition = (task: Task) => {
            if (task.timeConstraint) {
                depth += 1;
                const element = document.getElementById(task.id);
                if (!element) return;
                const { top, left, width, height } = element.getBoundingClientRect();

                const container = document.getElementById("graph-container");
                if (!container) return;
                const containerRect = container.getBoundingClientRect();

                const scrollLeft = container.scrollLeft;
                const scrollTop = container.scrollTop;

                const taskLeftX = left - containerRect.left + scrollLeft;
                const taskCenterX = left + width / 2 - containerRect.left + scrollLeft;
                const taskRightX = left + width - containerRect.left + scrollLeft;
                const taskCenterY = top + height / 2 - containerRect.top + scrollTop;
                const taskBottomY = top + height - containerRect.top + scrollTop;

                task.timeConstraint.forEach(tc => {
                    const subElement = document.getElementById(tc.task);
                    if (!subElement) return;
                    const subRect = subElement.getBoundingClientRect();
                    const subCenterX = subRect.left - containerRect.left + subRect.width / 2 + scrollLeft;

                    const parentTask = (task.parent === project.taskModel.rootTask.findTask(tc.task)?.parent) ? project.taskModel.rootTask.findTask(task.parent) : null;
                    const taskIndex = parentTask?.subtasks.indexOf(task);

                    let arrowWidth = 10;
                    let arrowHeight = 10;

                    //connect the tasks in a horizontal line, if they are direct siblings
                    if (parentTask && taskIndex !== undefined && (taskIndex > 0 && parentTask.subtasks[taskIndex - 1].id === tc.task)) {
                        const subRightX = subRect.left - containerRect.left + subRect.width + scrollLeft;
                        const subCenterY = subRect.top - containerRect.top + subRect.height / 2 + scrollTop;
                        newPathData += `M ${taskLeftX},${taskCenterY} V ${subCenterY} H ${subRightX}`;
                        allTextPositions.push({ x: subRightX - ((subRightX - taskLeftX) / 2), y: taskCenterY + 10, text: tc.naryOps });

                        //arrow to the left
                        let arrowBaseUpX = subRightX + arrowHeight;
                        let arrowBaseUpY = subCenterY + (arrowWidth / 2);
                        let arrowBaseDownX = subRightX + arrowWidth;
                        let arrowBaseDownY = subCenterY - (arrowHeight / 2);
                        newPathData += ` M ${arrowBaseUpX},${arrowBaseUpY} L ${subRightX},${subCenterY} L ${arrowBaseDownX},${arrowBaseDownY}`;

                    } else if (parentTask && taskIndex !== undefined && (taskIndex < parentTask.subtasks.length - 1 && parentTask.subtasks[taskIndex + 1].id === tc.task)) {
                        const subLeftX = subRect.left - containerRect.left + scrollLeft;
                        const subCenterY = subRect.top - containerRect.top + subRect.height / 2 + scrollTop;
                        newPathData += `M ${taskRightX},${taskCenterY} V ${subCenterY} H ${subLeftX}`;
                        allTextPositions.push({ x: subLeftX - ((subLeftX - taskRightX) / 2), y: taskCenterY + 10, text: tc.naryOps });

                        //arrow to the right
                        let arrowBaseUpX = subLeftX - arrowHeight;
                        let arrowBaseUpY = subCenterY + (arrowWidth / 2);
                        let arrowBaseDownX = subLeftX - arrowWidth;
                        let arrowBaseDownY = subCenterY - (arrowHeight / 2);
                        newPathData += ` M ${arrowBaseUpX},${arrowBaseUpY} L ${subLeftX},${subCenterY} L ${arrowBaseDownX},${arrowBaseDownY}`;

                    } else { //else connect them in a rectangular arc below the nodes
                        const subBottom = subRect.top - containerRect.top + subRect.height + scrollTop;
                        newPathData += `M ${taskCenterX},${taskBottomY} V ${taskBottomY + 20 + (depth * 8 % 32)} H ${subCenterX} V ${subBottom} H ${subCenterX}`;
                        allTextPositions.push({ x: subCenterX - ((subCenterX - taskCenterX) / 2), y: taskBottomY + 30 + (depth * 8 % 32), text: tc.naryOps  });

                        //arrow up
                        let arrowBaseLeftX = subCenterX - (arrowWidth / 2);
                        let arrowBaseLeftY = subBottom + arrowHeight;
                        let arrowBaseRightX = subCenterX + (arrowWidth / 2);
                        let arrowBaseRightY = subBottom + arrowHeight;
                        newPathData += ` M ${arrowBaseLeftX},${arrowBaseLeftY} L ${subCenterX},${subBottom} L ${arrowBaseRightX},${arrowBaseRightY}`;
                    }
                });
            }
            task.subtasks.forEach(subtask => calculatePosition(subtask));
        }
        calculatePosition(project.taskModel.rootTask);
        setConstraintTextData(allTextPositions);
        return newPathData;
    }

    // renders the whole graph of all tasks
    const renderNodes = (task: Task) => {
        const TaskEditorNodeProps = {
            handleAddButton: handleAddButton,
            handleEditButton: handleEditButton,
            collapseTask: (task: Task) => { setCollapsedTasksList([...collapsedTasksList, task.id]) },
            isDragging: isDragging,
            persistentContextMenu: persistentContextMenu,
            setPersistentContextMenu: (id: string) => setPersistentContextMenu(id),
        }

        // render grey task with plus icon to expand if task is collapsed
        if (collapsedTasksList.includes(task.id)) return (
            <div className="graphCell collapsedCell" key={task.id} id={`${task.id}`} onClick={() => setCollapsedTasksList(collapsedTasksList.filter(id => id !== task.id))}>
                <span>{task.name}</span>
                <div className={`graphIconBorder graphIconExpand`}>
                    <MDBIcon fas icon="plus" style={{ color: "#ffffff" }} />
                </div>
            </div>
        )
        // else use normal rendering
        return (
            <div className="graphCol" key={task.id}>
                {minimizeLeaves && task.subtasks.every((subtask: Task) => subtask.subtasks.length === 0) ?
                    <div className="graphCells">
                        <TaskEditorNode props={TaskEditorNodeProps} key={task.id} task={task} />
                        {task.subtasks.map((subtask: Task) => {
                            return <TaskEditorNode props={TaskEditorNodeProps} key={subtask.id} task={subtask} />
                        })}
                    </div>
                    :
                    <>
                        <TaskEditorNode props={TaskEditorNodeProps} key={task.id} task={task} />
                        <div className="graphRow">
                            {task.subtasks && task.subtasks.map((subtask: Task) => {
                                return renderNodes(subtask);
                            })}
                        </div>
                    </>
                }
            </div>
        )
    }

    // used on taskModel upload for initial creation and completion of the ideaBox
    const fillIdeaBox = (task: Task) => {
        const addIdeaBoxEntry = (task: Task, id: string, ie: string, type: TaskType) => {
            const entry = new IdeaBoxEntry();
            entry.task = task;
            entry.ruleId = id;
            entry.interfaceElement = ie;
            entry.id = nanoid();
            entry.type = type;
            const oldEntry = project.ideaBox.entries.find(e => e.ruleId === id);
            if(oldEntry) {
                Object.assign(oldEntry, entry);
            } else {
                project.ideaBox.entries.push(entry);
            }
        }

        if (task.rules.length > 0) {
            task.rules.forEach(rule => {
                rule.rhs.motor.forEach((motor) => {
                    addIdeaBoxEntry(task, rule.id, motor.variable, TaskType.INTERACTION)
                });
                rule.lhs.retrieves.forEach((retrieves) => {
                    addIdeaBoxEntry(task, rule.id, retrieves.variable, TaskType.INFORMATION)
                });
            });
        }
        if (task.subtasks && task.subtasks.length > 0) {
            for (const subtask of task.subtasks) {
                fillIdeaBox(subtask);
            }
        }
    }


    /* Dialog/Modal Control */

    // renders the TaskPropertiesDialog with the contents of the add menu; is passed to the TaskEditorNode and called from there
    const handleAddButton = (task: Task) => {
        setModalState(ModalState.ADD);
        setEditingTask(task);
    }

    // renders the TaskPropertiesDialog with the contents of the edit menu; is passed to the TaskEditorNode and called from there
    const handleEditButton = (task: Task) => {
        setModalState(ModalState.EDIT);
        setEditingTask(task);
    }

    // trigger TaskPropertiesDialog (edit) if editingTask is set from some distant component (e.g., IdeaBox or GlyphGrouping)
    useEffect(() => {
        if (editingTask.id && modalState !== ModalState.ADD) setModalState(ModalState.EDIT);
        // modalState intentionally omitted to avoid infinite loop when setting modalState
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingTask])


    /* File Handling */

    // event handler for upload button. This is necessary to have a custom upload button.
    const handleButtonClick = (e: any) => {
        e.preventDefault();
        //@ts-ignore
        fileInputRef.current.click();
    }

    // requests the server to process the uploaded file
    const processUpload = async () => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(process.env.REACT_APP_API_URI + '/importTaskHierarchy', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const { htm }: any = res.data;
            project.taskModel = htm;
            fillIdeaBox(project.taskModel.rootTask);
            setNodes(renderNodes(project.taskModel.rootTask));
            UserControlService.save()
        } catch (err: any) {
            if (err.response.status === 500) {
                UserControlService.showInfoPopup({ title: "Server Error", content: `There was a problem with the server.`, duration: 5000, color: "danger" });
            } else {
                UserControlService.showInfoPopup({ title: "Error", content: err.response.data.msg, duration: 5000, color: "danger" });
            }
        }
    }

    // handles importing hierarchy from tab-indented text
    const handleTextImport = () => {
        try {
            if (!importText.trim()) {
                throw new Error("Please enter hierarchy text");
            }

            const rootTask = parseTabIndentedHierarchy(importText);

            // Confirm if existing hierarchy will be replaced
            if (project.taskModel.rootTask.name &&
                !window.confirm("This will replace the existing hierarchy. Continue?")) {
                return;
            }

            UserControlService.update();
            project.taskModel.rootTask = rootTask;
            fillIdeaBox(project.taskModel.rootTask);
            setNodes(renderNodes(project.taskModel.rootTask));
            UserControlService.save();

            setShowTextImportModal(false);
            setImportText("");

            UserControlService.showInfoPopup({
                title: "Import Successful",
                content: `Imported hierarchy successfully!`,
                duration: 3000
            });
        } catch (err: any) {
            UserControlService.showInfoPopup({
                title: "Import Error",
                content: err.message || "Failed to parse hierarchy",
                duration: 5000,
                color: "danger"
            });
        }
    }

    // converts the taskModel into a string to then get exported as an XML file
    const generateXML = () => {
        const esc = (str: string): string => {
            return str ? str.replace(/[<>"'&]/g, (char) => ({
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                '\'': '&apos;',
                '&': '&amp;'
            }[char] || char)) :  "";
        }

        const tm = project.taskModel;
        
        // Agents
        const agents = tm.agents.reduce((str, agent) => str + `<agents name="${esc(agent.name)}"/>`, "");

        // Tasks
        const parseRule = (rule: Rule, task: Task, taskIdentifier: string) => {
            if (rule.rhs.percept.length > 0) {
                return `<rule xsi:type="tm_1:PerceptRule" id="${esc(rule.id)}"><lookAt perceivedObject="${esc(rule.rhs.percept[0].perceivedObject)}" ${rule.rhs.percept[0].workload ? `workload="${esc(rule.rhs.percept[0].workload)}"` : ""}/></rule>`;
            }

            const retrieves = rule.lhs.retrieves.reduce((str, retrieves) => str + `<retrieves ${retrieves.workload ? `workload="${esc(retrieves.workload)}"` : ""} variable="${esc(retrieves.variable)}" retrieval="${esc(retrieves.retrieval)}" />`, "");
            const conditions = rule.lhs.condition.reduce((str, condition) => str + `<conditions ${condition.workload ? `workload="${esc(condition.workload)}"` : ""} condition="${esc(condition.condition)}"/>`, "");
            const LHS = `<LHS>${retrieves + conditions}</LHS>`;
            
            const motor = rule.rhs.motor.reduce((str, motor) => str + `<motor ${motor.workload ? `workload="${esc(motor.workload)}"` : ""} ${motor.resource ? `resource="${esc(motor.resource)}"` : ""} ${motor.action ? `action="${esc(motor.action)}"` : ""} variable="${esc(motor.variable)}" ${motor.value ? `value="${esc(motor.value)}"` : ""} ${motor.feedback ? `feedback="${esc(motor.feedback)}"` : ""}/>`, "");
            const memorize = rule.rhs.memorize.reduce((str, memorize) => str + `<memorize ${memorize.workload ? `workload="${esc(memorize.workload)}"` : ""} variable="${esc(memorize.variable)}" ${memorize.value ? `value="${esc(memorize.value)}"` : ""} />`, "");
            const speak = rule.rhs.speak.reduce((str, speak) => str + `<speak ${speak.target ? `target="//@agents.${"" + (speak.target.id - 1)}"`: ""} text="${esc(speak.text)}" message="${esc(speak.message)}"/>`, "");

            const subgoals = (task.subtasks && task.subtasks.length > 0) ? task.subtasks.reduce((str, subtask, index) => str + `<subgoals task="${taskIdentifier + '/@subtask.' + index}"/>`, "") : "";
            const RHS = `<RHS>${motor + memorize + speak + subgoals}</RHS>`;
            return !(retrieves || conditions || motor || memorize || speak || subgoals) ? `<rule xsi:type="tm_1:WaitingRule" id="${esc(rule.id)}" />` : `<rule xsi:type="tm_1:RegularRule" id="${esc(rule.id)}">${LHS + RHS}</rule>`;
        }

        const taskIdentifierMap = new Map();
        const mapTaskIdentifiers = (task: Task, taskIdentifier: string, index: number) => {
            if (task.id === tm.rootTask.id) {
                taskIdentifierMap.set(task.id, "//@tasks.0");
                task.subtasks.forEach((subtask, subIndex) => {
                    mapTaskIdentifiers(subtask, `${taskIdentifier}`, subIndex)
                });
            } 
            else {
                taskIdentifierMap.set(task.id, `${taskIdentifier}/@subtask.${index}`);
                task.subtasks.forEach((subtask, subIndex) => {
                    mapTaskIdentifiers(subtask, `${taskIdentifier}/@subtask.${index}`, subIndex)
                });
            }
        }
        mapTaskIdentifiers(tm.rootTask, "//@tasks.0", 0);

        const parseTask = (task: Task, taskIdentifier: string):string => {
            return `<${task.id === tm.rootTask.id ? "tasks" : "subtask"} identifier="${esc(task.id)}" name="${esc(task.name)}" description="${esc(task.nr)}" frequency="null" ${task.agents.length > 0 ? `agent="${task.agents.reduce((str, agent) => (str + "//@agents." + (agent.id - 1) + " "), "").trim()}"` : ""} ${task.id !== tm.rootTask.id ? `parent="${taskIdentifier.substring(0, taskIdentifier.lastIndexOf('/'))}"` : ''}>
                ${task.rules.reduce((str, rule) => str + parseRule(rule, task, taskIdentifier), "")}
                ${(task.subtasks && task.subtasks.length > 0) ? task.subtasks.reduce((str, subtask, index) => str + parseTask(subtask, taskIdentifier + `/@subtask.${index}`), "") : ""}
                ${(task.timeConstraint && task.timeConstraint.length > 0) ? task.timeConstraint.reduce((str, constraint) => str + `<timeConstraint naryOps="${esc(constraint.naryOps)}" task="${taskIdentifierMap.get(constraint.task)}"/>`, "") : ""}
            </${task.id === tm.rootTask.id ? "tasks" : "subtask"}>`
        }
        const tasks = parseTask(tm.rootTask, "//@tasks.0");


        // Resources
        const types = tm.resourceModel.typeDefinition.reduce((str, type) => str + `<types name="${esc(type.name)}">${type.attributes.reduce((str, attribute) => str + `<attributes name="${esc(attribute.name)}" ${attribute.type ? `type="${esc(attribute.type)}"` : ''} ${attribute.mode ? `mode="${esc(attribute.mode)}"` : ''} />`, "")} </types>`, "");
        const instances = tm.resourceModel.instances.reduce((str, instance) => str + `<instances name="${esc(instance.name)}" type="//@resources/@types.${instance.type.id - 1 + ""}"/>`, "");
        const resources = `<resources>${types + instances}</resources>`;

        return `<tm:HierarchicalTaskModel xmi:version="2.0" xmlns:xmi="http://www.omg.org/XMI" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:tm="http://www.holides.eu/taskmodel" xmlns:tm_1="http://www.holides.eu/taskmodel/rules" mainTask="//@tasks.0">${agents + tasks + resources}</tm:HierarchicalTaskModel>`;
    }

    // called when user wants to download/export XML of the taskModel
    const downloadXML = () => {
        const YYYYMMDDHHMMSS = new Date().toISOString().slice(0,19).replace(/[-T:]/g, '');
        const filename = `${project.name.replace(/\s/g, '')}_${YYYYMMDDHHMMSS}.xml`;
        const xml = generateXML();

        var blob = new Blob([xml], { type: 'application/xml' });
        FileSaver.saveAs(blob, filename);
        UserControlService.showInfoPopup({ title: "XML Export Successful", content: `Successfully exported XML of project ${project.name}!`, duration: 5000 });
    }


    return (
        <div id="task-editor-container">
            {!props.hideMenu && <div className="menuBar shadow-5">
                <div className="menuBarItem">
                    <span className="menuBarTitle">Upload</span>
                    <form id="upload-form" className="menuBarEntries">
                        <input type='file' id='customFile' ref={fileInputRef} onChange={(e: any) => { setFile(e.target.files[0]) }} />
                        <MDBBtn type='submit' onClick={handleButtonClick} className='btn btn-primary btn-block' title="Upload a .taskhierarchy-file to the project">Select Taskmodel</MDBBtn>
                        <MDBBtn
                            type='button'
                            onClick={() => setShowTextImportModal(true)}
                            className='btn btn-primary btn-block mt-2'
                            title="Import hierarchy from tab-indented text (e.g., from PowerPoint)">
                            Import from Text
                        </MDBBtn>
                    </form>
                </div>
                <div className="menuBarItem">
                    <span className="menuBarTitle">Settings</span>
                    <div className="menuBarEntries">
                        <MDBBtn disabled={!collapsedTasksList.length} type='submit' onClick={() => setCollapsedTasksList([])} className='btn btn-primary btn-block' title="Expands all collapsed Tasks">Expand All</MDBBtn>
                        <div className="d-flex flex-column gap-1">
                            <MDBSwitch onChange={() => { 
                                setMinimizeLeaves(!minimizeLeaves); 
                                setProject(project);
                                }} defaultChecked={minimizeLeaves} label="Minimize Leaves" />
                            <MDBSwitch onChange={() => { 
                                setShowConstraintArrows(!showConstraintArrows); 
                                setProject(project);
                                }} defaultChecked={showConstraintArrows} label="Constraint Arrows" />
                        </div>
                    </div>
                </div>
                <div className="menuBarItem">
                    <span className="menuBarTitle">Key</span>
                    <div className="menuBarEntries">
                    <div className="d-flex flex-row gap-1">
                            <div className={`graphIconBorder graphIconInformation`} style={{ borderRadius: "500px" }}>
                                <MDBIcon fas icon="eye" style={{ color: "#ffffff" }} />
                            </div>
                            Perception
                        </div>
                        <div className="d-flex flex-row gap-1">
                            <div className={`graphIconBorder graphIconInteraction`} style={{ borderRadius: "500px" }}>
                                <MDBIcon fas icon="hand-paper" style={{ color: "#ffffff" }} />
                            </div>
                            Interaction
                        </div>
                    </div>
                </div>
                <div className="menuBarItem">
                    <span className="menuBarTitle">Export</span>
                    <div className="menuBarEntries">
                        <MDBBtn type='submit' onClick={downloadXML} className='btn btn-primary btn-block' title="Download the taskmodel as an XML file">
                            Download XML
                        </MDBBtn>
                    </div>
                </div>
            </div>}

            <div id="graph-container" ref={containerRef}>
                <svg id="svg-container" style={{ height: graphDimensions.height, width: graphDimensions.width }}>
                    <path d={graphPathData} stroke="black" fill="none" />
                    <path d={constraintPathData} stroke="#3bacb7" strokeWidth="2px" fill="none" />
                    {constraintTextData.map((entry, index) => {
                        return <text key={index + '_' + entry.x + entry.y} x={entry.x} y={entry.y} fill="#3bacb7" textAnchor="middle" fontSize="10px">{entry.text}</text>
                    })}
                </svg>
                <div id="node-container">
                    {nodes}
                </div>
            </div>
            {modalState !== ModalState.HIDDEN && <TaskPropertiesDialog
                modalState={modalState}
                setModalState={setModalState}
            />}
            {showTextImportModal && <MDBModal show={showTextImportModal} staticBackdrop tabIndex="-1">
                <MDBModalDialog>
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>Import Hierarchy from Text</MDBModalTitle>
                            <MDBBtn
                                className='btn-close'
                                color='none'
                                onClick={() => setShowTextImportModal(false)}
                            />
                        </MDBModalHeader>
                        <MDBModalBody>
                            <p>Paste your tab-indented hierarchy (e.g., from PowerPoint Smart Art):</p>
                            <textarea
                                className="form-control"
                                value={importText}
                                onChange={(e) => setImportText(e.target.value)}
                                rows={15}
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                                placeholder={"Root\n\tChild 1\n\t\tGrandchild\n\tChild 2"}
                            />
                            <small className="text-muted mt-2 d-block">
                                Tip: Use tabs or consistent spacing for hierarchy levels. First line must be the root task.
                            </small>
                        </MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn
                                type='button'
                                color="primary"
                                onClick={handleTextImport}
                                disabled={!importText.trim()}>
                                Import
                            </MDBBtn>
                            <MDBBtn
                                type='button'
                                color="secondary"
                                onClick={() => setShowTextImportModal(false)}>
                                Cancel
                            </MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>}
            {isDragging && draggedTask && createPortal(<div style={{ position: 'absolute', left: cloneX, top: cloneY }}>{renderClones(draggedTask)}</div>, document.body)}
        </div>
    );
}

export default TaskEditor2;
